import React from "react";

import clickup, { type ClickupStreamIntegrationDetails } from ".";

import { useSyncedPersistentStateWithOverride } from "~/actionItems/hooks/useSyncedPersistentState";
import { ClickupLogo } from "~/integrations/clickup/ClickupLogo";
import { FolderSelector } from "~/integrations/clickup/selectors/ClickupFolderSelector";
import { ListSelector } from "~/integrations/clickup/selectors/ClickupListSelector";
import { SpaceSelector } from "~/integrations/clickup/selectors/ClickupSpaceSelector";
import { WorkspaceSelector } from "~/integrations/clickup/selectors/ClickupWorkspaceSelector";
import {
	type ActionItemIntegrationCardProps,
	connectStreamIntegration,
	type IntegrationConfigProps,
	StreamSettingsIntegrationCard,
} from "~/stream/StreamSettings/ActionItemIntegrations";
import { Checkbox } from "~/ui";
import { Button } from "~/ui";

import { getIntegrationForKey } from "../actionItemIntegration";
import { clickupStore } from "./store";

import styles from "./css/styles.module.css";

export function ClickupStreamSettingsIntegrationCard({ viewer, onClickIntegration }: ActionItemIntegrationCardProps) {
	const onClick = React.useCallback(() => {
		onClickIntegration(getIntegrationForKey("CLICKUP"));
	}, [onClickIntegration]);

	return <StreamSettingsIntegrationCard integration={clickup} onClickIntegration={onClick} viewer={viewer} />;
}

export function ClickupStreamSettingsConfigView({
	integration,
	stream,
	onFinish,
	onCancel,
	integrationDetails,
}: IntegrationConfigProps<ClickupStreamIntegrationDetails>) {
	const [workspaceId, setWorkspaceId] = useSyncedPersistentStateWithOverride<string | undefined>(
		clickupStore,
		"workspaceId",
		integrationDetails?.externalWorkspaceId,
	);
	const [spaceId, setSpaceId] = useSyncedPersistentStateWithOverride<string | undefined>(
		clickupStore,
		"spaceId",
		integrationDetails?.externalSpaceId,
	);
	const [folderId, setFolderId] = useSyncedPersistentStateWithOverride<string | undefined>(
		clickupStore,
		"folderId",
		integrationDetails?.externalFolderId,
	);
	const [listId, setListId] = useSyncedPersistentStateWithOverride<string | undefined>(
		clickupStore,
		"listId",
		integrationDetails?.externalListId,
	);
	const [listName, setListName] = React.useState<string>();
	const [includeAiGenerated, setIncludeAiGenerated] = React.useState<boolean>(
		integrationDetails?.includeAiGenerated ?? true,
	);

	const handleIncludeAiGeneratedChange = React.useCallback((checked: boolean) => {
		setIncludeAiGenerated(checked);
	}, []);

	const submit = React.useCallback(async () => {
		if (!listId || !workspaceId || !stream || !stream.id || !listName) {
			throw new Error("One or more fields are invalid");
		}

		/* eslint-disable camelcase */
		const destinationInfo = {
			list_id: listId,
			workspace_id: workspaceId,
			list_name: listName,
			include_ai_generated: includeAiGenerated,
		};

		/* eslint-enable camelcase */

		return connectStreamIntegration("CLICKUP", destinationInfo, stream.id).then(() => {
			onFinish(integration);
		});
	}, [listId, workspaceId, stream, integration, listName, includeAiGenerated, onFinish]);

	return (
		<>
			<ClickupLogo />
			<div className={styles.settingsTitle}>Select a Clickup List</div>
			<p>
				Sync action items between the note series <b>{stream?.displayName}</b> and ClickUp.
			</p>
			<div className={styles.configSettingsContainer}>
				<div className={styles.workspace}>
					<WorkspaceSelector workspaceId={workspaceId} setWorkspaceId={setWorkspaceId} />
				</div>
				<div className={styles.space}>
					<SpaceSelector workspaceId={workspaceId} spaceId={spaceId} setSpaceId={setSpaceId} />
				</div>
				<div className={styles.folder}>
					<FolderSelector spaceId={spaceId} folderId={folderId} setFolderId={setFolderId} />
				</div>
				<div className={styles.list}>
					<ListSelector
						spaceId={spaceId}
						folderId={folderId}
						listId={listId}
						setListId={setListId}
						setListName={setListName}
					/>
				</div>
			</div>
			<div className={styles.toggleOption}>
				<Checkbox
					id="include-ai-generated"
					checked={includeAiGenerated}
					onCheckedChange={handleIncludeAiGeneratedChange}
				>
					Include AI-generated action items
				</Checkbox>
				<span className={styles.toggleDescription}>
					When enabled, AI-generated action items will be sent to this integration.
				</span>
			</div>
			<p>
				Action items <b>created after connecting</b> will be sent to ClickUp.
			</p>
			<div className={styles.buttonRow}>
				<Button size="normal" onClick={onCancel}>
					Cancel
				</Button>
				<Button size="normal" variant="primary" onClick={submit} disabled={!listId}>
					Connect
				</Button>
			</div>
		</>
	);
}
